import type { AreaData } from '@revelio/replots';

import type { DistributionDataSet } from '../../../../DataProvider/types';
import type { DataConverter, SheetData } from '../types';
import { formatColumnName } from './utils';

/**
 * Convert DistributionDataSet to area chart format
 * Used for KDE/distribution charts that should be displayed as area charts
 */
export const convertAreaDataToSheet: DataConverter<DistributionDataSet> = ({
  data,
  primaryDimension,
}): SheetData[] => {
  const formattedPrimaryDimension = formatColumnName(primaryDimension);

  // Convert to area data format first
  const areaData: AreaData[] = data.data
    .filter((d) => d && d.dim && d.distribution && d.distribution.length > 0)
    .map((d) => ({
      label: d.dim,
      values: d.distribution
        .filter(
          (v) =>
            v &&
            typeof v.value === 'number' &&
            typeof v.kde === 'number' &&
            typeof v.cdf === 'number' &&
            // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
            !isNaN(v.value) &&
            // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
            !isNaN(v.kde) &&
            // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
            !isNaN(v.cdf)
        )
        .map((v) => ({
          x: v.value,
          value: v.kde,
          secondaryValue: v.cdf,
        })),
    }))
    .filter((d) => d.values.length > 0);

  // Convert area data to sheet format
  const allXValues = Array.from(
    new Set(
      areaData.flatMap((series) =>
        series.values.map((point) => point.x.toString())
      )
    )
  )
    .sort((a, b) => Number(a) - Number(b))
    .filter((x) => x !== null);

  const seriesMap = new Map(
    areaData.map((series) => [
      series.label,
      new Map(series.values.map((point) => [point.x.toString(), point.value])),
    ])
  );

  return allXValues.map((xValue) => ({
    [formattedPrimaryDimension]: xValue,
    ...Object.fromEntries(
      areaData.map((series) => [
        formatColumnName(series.label),
        seriesMap.get(series.label)?.get(xValue) ?? 0,
      ])
    ),
  }));
};
